import { ApolloError, useMutation, useQuery } from "@apollo/client";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { useRef, useState } from "react";
import { useCanAccess, useNotify, useTranslate } from "react-admin";

import { ResourceName } from "../../constants/resources";
import {
  CrmObject,
  CustomerServiceAccountManagersDocument,
  type CustomerServiceAccountManagersQuery,
  type CustomerServiceAccountManagersQueryVariables,
  InviteManagerDocument,
  type InviteManagerMutation,
  type InviteManagerMutationVariables,
  SetAccountAmbulantDocument,
  type SetAccountAmbulantMutation,
  type SetAccountAmbulantMutationVariables,
} from "../../generated/graphql";
import { CRM_COLORS, CRM_OBJECT_COLORS, formatCrmDateTime } from "../../lib/crm";
import { getInvitationStatusColor } from "../../lib/statusColors";
import { space } from "../../theme/designTokens";
import { dialogContentSx, dialogTitleSx } from "../../theme/dialogStyles";
import { AccountPortalAccess } from "./AccountPortalAccess";
import { CrmIconTile } from "./CrmObjectIcon";
import { CrmFieldList, CrmSection } from "./CrmSection";
import { CrmTableCard } from "./CrmTableCard";

type AccountManagers = CustomerServiceAccountManagersQuery["customerServiceAccountManagers"];
type AccountContact = AccountManagers["contacts"][number];

const canInviteContact = (contact: AccountContact): boolean =>
  Boolean(contact.email) && ["AVAILABLE", "PENDING"].includes(contact.invitationState);

const INVITE_ERROR_KEYS: Record<string, string> = {
  "email-already-registered-error": "crm.accountManagers.dialog.loginConflict",
  "not-licensing-manager": "crm.accountManagers.dialog.notAccountContact",
  "invalid-manager-organization": "crm.accountManagers.dialog.invalidOrganization",
  "ambiguous-customer-email": "crm.accountManagers.dialog.ambiguousEmail",
};

const errorKey = (error: unknown, keys: Record<string, string>, fallback: string): string => {
  const code =
    error instanceof ApolloError ? error.graphQLErrors[0]?.message : error instanceof Error ? error.message : undefined;
  return (code && keys[code]) || fallback;
};

const dialogActionsSx = { px: `${space.xxl}px`, pb: `${space.xl}px` };

const InviteManagerDialog = ({
  accountName,
  managers,
  onClose,
  onInvited,
}: {
  accountName: string;
  managers: AccountManagers;
  onClose: () => void;
  onInvited: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const eligibleContacts = managers.contacts.filter(canInviteContact);
  const [contactId, setContactId] = useState<string>(() =>
    new Set(eligibleContacts.map(contact => contact.email)).size === 1 ? eligibleContacts[0].contactId : ""
  );
  const selectedContact = managers.contacts.find(
    contact => contact.contactId === contactId && canInviteContact(contact)
  );
  const sending = useRef(false);
  const [invite, { loading }] = useMutation<InviteManagerMutation, InviteManagerMutationVariables>(
    InviteManagerDocument
  );
  const canSend = Boolean(selectedContact?.email) && !loading;

  const send = async () => {
    if (!canSend || !selectedContact?.email || sending.current) return;
    sending.current = true;
    try {
      await invite({
        variables: {
          salesforceAccountId: managers.salesforceAccountId,
          email: selectedContact.email,
        },
      });
      notify(translate("crm.accountManagers.dialog.success"), { type: "success" });
      onInvited();
    } catch (error) {
      notify(translate(errorKey(error, INVITE_ERROR_KEYS, "crm.accountManagers.dialog.error")), { type: "error" });
    } finally {
      sending.current = false;
    }
  };

  const contactLabel = (contact: AccountContact) =>
    [
      contact.name,
      contact.email,
      contact.invitationState === "AVAILABLE"
        ? null
        : translate(`crm.accountContacts.invitationState.${contact.invitationState}`),
    ]
      .filter(Boolean)
      .join(" · ");

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="invite-manager-title">
      <DialogTitle id="invite-manager-title" sx={dialogTitleSx}>
        {translate("crm.accountManagers.dialog.title")}
      </DialogTitle>
      <DialogContent sx={dialogContentSx}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {translate("crm.accountManagers.dialog.description")}{" "}
          {managers.organization
            ? translate("crm.accountManagers.dialog.joins", { name: accountName })
            : translate("crm.accountManagers.dialog.createsOrganization", { name: accountName })}
        </Typography>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <FormLabel id="invite-manager-contact">{translate("crm.accountManagers.dialog.contact")}</FormLabel>
          <RadioGroup
            aria-labelledby="invite-manager-contact"
            value={contactId}
            onChange={event => setContactId(event.target.value)}
          >
            {managers.contacts.map(contact => (
              <FormControlLabel
                key={contact.contactId}
                value={contact.contactId}
                disabled={!canInviteContact(contact)}
                control={<Radio size="small" />}
                label={contactLabel(contact)}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </DialogContent>
      <DialogActions sx={dialogActionsSx}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          {translate("crm.accountManagers.dialog.cancel")}
        </Button>
        <Button variant="contained" onClick={send} disabled={!canSend} sx={{ textTransform: "none" }}>
          {translate("crm.accountManagers.dialog.send")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Whether the account provides ambulant care, on the account's Details tab.
export const AccountAmbulantCheckbox = ({ managers }: { managers: AccountManagers }) => {
  const translate = useTranslate();
  const notify = useNotify();
  // The chosen value shows until the account's refetch confirms it.
  const [pending, setPending] = useState<boolean | null>(null);
  const [setAmbulant] = useMutation<SetAccountAmbulantMutation, SetAccountAmbulantMutationVariables>(
    SetAccountAmbulantDocument,
    { refetchQueries: ["CustomerServiceAccountManagers"], awaitRefetchQueries: true }
  );

  const change = async (ambulant: boolean) => {
    setPending(ambulant);
    try {
      await setAmbulant({ variables: { salesforceAccountId: managers.salesforceAccountId, ambulant } });
      notify(translate("crm.accountAmbulant.saved"), { type: "success" });
    } catch {
      notify(translate("crm.accountAmbulant.error"), { type: "error" });
    } finally {
      setPending(null);
    }
  };

  return (
    <Checkbox
      size="small"
      checked={pending ?? managers.ambulant}
      disabled={pending !== null}
      onChange={event => void change(event.target.checked)}
      inputProps={{ "aria-label": translate("crm.accountAmbulant.label") }}
      sx={{ p: 0 }}
    />
  );
};

const AccountManagersContent = ({
  accountName,
  managers,
  onChanged,
}: {
  accountName: string;
  managers: AccountManagers;
  onChanged: () => void;
}) => {
  const translate = useTranslate();
  const [inviting, setInviting] = useState(false);
  const invitationPermission = useCanAccess({ action: "list", resource: ResourceName.Invitation });
  const canInvite = invitationPermission.canAccess === true && !invitationPermission.isPending;
  const hasContacts = managers.contacts.length > 0;

  const portalAccess = (
    <AccountPortalAccess
      organization={managers.organization}
      salesforceAccountId={managers.salesforceAccountId}
      onSetup={onChanged}
    />
  );

  return (
    <Box data-testid="crm-account-managers">
      <CrmSection id="crm-account-portal" title={translate("crm.accountManagers.portal.title")}>
        <CrmFieldList
          items={[
            {
              key: "status",
              label: translate("crm.accountManagers.portal.status"),
              value: translate(
                managers.organization ? "crm.accountManagers.portal.ready" : "crm.accountManagers.portal.notReady"
              ),
            },
          ]}
        />
        {managers.managers.length === 0 && (
          <Box sx={{ px: "16px", pt: "12px" }}>
            <Alert severity="warning" data-testid="crm-account-managers-no-access">
              {translate("crm.accountManagers.portal.noAccess")}
            </Alert>
          </Box>
        )}
        {!managers.organization && portalAccess}
        {!hasContacts && (
          <Typography sx={{ px: "16px", pt: "8px", fontSize: "13px", color: CRM_COLORS.textWeak }}>
            {translate("crm.accountManagers.noContacts")}
          </Typography>
        )}
      </CrmSection>
      {managers.organization && portalAccess}
      <Box sx={{ display: "grid", gap: "12px" }}>
        <CrmTableCard
          testId="crm-account-managers-managers"
          title={translate("crm.accountManagers.managers.title")}
          icon={
            <CrmIconTile Icon={ManageAccountsOutlinedIcon} color={CRM_OBJECT_COLORS[CrmObject.Contact]} size={24} />
          }
          actions={
            canInvite && (
              <Button
                variant="outlined"
                size="small"
                disabled={!hasContacts}
                onClick={() => setInviting(true)}
                sx={{ textTransform: "none", backgroundColor: CRM_COLORS.cardBackground }}
              >
                {translate("crm.accountManagers.invite")}
              </Button>
            )
          }
          empty={translate("crm.accountManagers.managers.empty")}
          headers={[
            translate("crm.accountManagers.managers.name"),
            translate("crm.accountManagers.managers.email"),
            translate("crm.accountManagers.managers.registeredAt"),
            translate("crm.accountManagers.managers.lastSignInAt"),
          ]}
          rows={managers.managers.map(manager => ({
            key: String(manager.userId),
            cells: [
              manager.name,
              manager.email ?? "",
              formatCrmDateTime(manager.registeredAt),
              manager.lastSignInAt
                ? formatCrmDateTime(manager.lastSignInAt)
                : translate("crm.accountManagers.managers.never"),
            ],
          }))}
        />
        <CrmTableCard
          testId="crm-account-managers-invitations"
          title={translate("crm.accountManagers.invitations.title")}
          icon={<CrmIconTile Icon={MailOutlineIcon} color={CRM_OBJECT_COLORS[CrmObject.Lead]} size={24} />}
          empty={translate("crm.accountManagers.invitations.empty")}
          headers={[
            translate("crm.accountManagers.invitations.email"),
            translate("crm.accountManagers.invitations.createdAt"),
            translate("crm.accountManagers.invitations.status"),
          ]}
          rows={managers.invitations.map(invitation => ({
            key: String(invitation.id),
            cells: [
              invitation.email,
              formatCrmDateTime(invitation.createdAt),
              <Chip
                key="status"
                size="small"
                label={translate(`invitationStatus.${invitation.status.toLowerCase()}`)}
                color={getInvitationStatusColor(invitation.status)}
              />,
            ],
          }))}
        />
      </Box>
      {inviting && (
        <InviteManagerDialog
          accountName={accountName}
          managers={managers}
          onClose={() => setInviting(false)}
          onInvited={() => {
            setInviting(false);
            onChanged();
          }}
        />
      )}
    </Box>
  );
};

// The account's portal access, managers and invitations. Accounts that are
// not B2B come back ineligible, and the record page offers no tab for them.
export const useAccountManagers = (salesforceId: string, skip: boolean) =>
  useQuery<CustomerServiceAccountManagersQuery, CustomerServiceAccountManagersQueryVariables>(
    CustomerServiceAccountManagersDocument,
    { variables: { salesforceAccountId: salesforceId }, fetchPolicy: "cache-and-network", skip }
  );

type AccountManagersCardProps = {
  accountName: string;
  result: ReturnType<typeof useAccountManagers>;
};

export const AccountManagersCard = ({ accountName, result }: AccountManagersCardProps) => {
  const translate = useTranslate();
  const { data, loading, error, refetch } = result;
  const managers = data?.customerServiceAccountManagers;

  if (error) return <Alert severity="error">{translate("crm.accountManagers.loadError")}</Alert>;
  if (loading && !managers) return <CircularProgress size={20} aria-label={translate("crm.loading")} />;
  if (!managers?.eligible) return null;
  return <AccountManagersContent accountName={accountName} managers={managers} onChanged={() => refetch()} />;
};
